import { useCallback, useEffect, useState } from 'react';

import { ApiGetCompanyType } from '../../../api/types/apiGetCompany';
import { TextField } from '../../../components';
import Button from '../../../components/Buttons/Button';
import cn from '../../../lib/classNames';
import Layout from '../../Layout/LayoutStarted';

import styles from './Step1.module.scss';

const isValidDomain = (domain: string): boolean => {
	const domainRegex =
		/^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/i;
	return domainRegex.test(domain);
};

type Step2Type = {
	setEmailDomainValue: (data: string[]) => void;
	companyInfo: ApiGetCompanyType;
};

export default function Step2(props: Step2Type) {
	const [emailDomainValue, setEmailDomainValue] = useState<string[]>([]);
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [isEmailError, setIsEmailError] = useState(false);
	const [companyData, setCompanyData] = useState<ApiGetCompanyType | null>(props.companyInfo);

	const { emailDomains = [] } = companyData || {};

	const reset = useCallback(() => {
		setEmailDomainValue(emailDomains || []);
		setIsEmailError(false);
	}, [companyData]);

	useEffect(() => {
		reset();
	}, [companyData]);

	const editEmailDomain = useCallback(
		(index: number, value: string) => {
			setIsEmailError(false);
			setEmailDomainValue((state) => {
				const newState = [...state];
				newState[index] = value;
				return newState;
			});
		},
		[setEmailDomainValue],
	);

	const addEmailDomain = useCallback(() => {
		setEmailDomainValue((state) => {
			return [...state, ''];
		});
	}, [setEmailDomainValue]);

	const removeEmailDomain = useCallback(
		(index: number) => {
			setEmailDomainValue((state) => {
				const newState = [...state];
				newState.splice(index, 1);
				return newState;
			});
		},
		[setEmailDomainValue],
	);

	const handleSubmit = useCallback(() => {
		setIsSubmitted(true);
		setIsEmailError(false);

		const isInvalidEmail = emailDomainValue.find((email) => !isValidDomain(email));
		if (isInvalidEmail) {
			setIsEmailError(true);
		} else {
			props.setEmailDomainValue(emailDomainValue);
		}
	}, [emailDomainValue]);

	return (
		<Layout displayRightBlock={false} displayFooter>
			<div className={cn(styles['company-wrap'])}>
				<div className={cn(styles['form-wrap'], styles.center)}>
					<div className={styles['title-wrap']}>
						<h3 className={styles.title}>Set Your Company’s Allowed Domains</h3>
					</div>

					<h4 className={styles['sub-title']}>
						To set up your corporate subscription, please specify the allowed domains for your company. Employees using these domains will be able to register.
					</h4>

					<div className={styles.body}>
						<div className={styles['field-inline']}>
							<div className={cn(styles['field-wrap'], styles['field-wrap-gender'])}>
								<div className={cn(styles['field-inner'], styles['field-required'])}>
									<TextField
										label="Company Domain"
										placeholder="Enter your domain"
										value={emailDomainValue?.[0] || ''}
										onChange={(value: string) => editEmailDomain(0, value)}
									/>

									{emailDomainValue.slice(1).map((emailDomain, index) => (
										// eslint-disable-next-line react/no-array-index-key
										<div key={index} className={styles['domain-wrapper']}>
											<TextField
												value={emailDomain}
												onChange={(value: string) => editEmailDomain(index + 1, value)}
											/>

											<a
												className={styles['remove-domain-button']}
												onClick={() => removeEmailDomain(index + 1)}
											>
												✕
											</a>
										</div>
									))}
									{isSubmitted && isEmailError && (
										<p className={styles['field-error']}>Enter a valid domain</p>
									)}
									<a className={styles['add-domain']} onClick={addEmailDomain}>
										+ Add domain
									</a>
								</div>
							</div>
						</div>

						<div className={cn(styles['field-inline'], styles.buttons)}>
							<Button onClick={handleSubmit} variant="filled" color="primary" sizeWidth="full">
							START EXPLORING
							</Button>
						</div>
					</div>
				</div>
			</div>
		</Layout>
	);
}
